import { computed, type MaybeRefOrGetter } from 'vue'

import { useLearnerApiQueryStore, type LearnerApiQueryStoreOptions } from '../stores/learner-api.query-store'

/**
 * Learner API Composable
 *
 * Provides access to learner-related queries and mutations.
 * This is a simple wrapper around the query store that follows the
 * standard composable → query store pattern.
 *
 * Returns actual TanStack Query objects (not wrapped in try-catch)
 * to preserve all query methods including suspense() for SSR.
 *
 * No singleton needed - TanStack Query handles deduplication based on query keys.
 */
export const useLearnerApi = (
  courseId: MaybeRefOrGetter<string>,
  courseLocationId: MaybeRefOrGetter<string>,
  options?: LearnerApiQueryStoreOptions
) => {
  const {
    fetchNextIncompleteLessonQuery,
    createNextIncompleteLessonForAllCoursesMutation,
    fetchUnlockedModulesQuery,
    fetchCompletedLessonsQuery,
    fetchSalesDataQuery,
  } = useLearnerApiQueryStore(courseId, courseLocationId, options)

  // Return query factory functions directly - no try-catch wrapping
  // TanStack Query handles errors internally via error states
  const getNextInCompleteLesson = (lessonId?: MaybeRefOrGetter<string>) => {
    return fetchNextIncompleteLessonQuery(lessonId)
  }

  const getUnlockedModules = () => {
    return fetchUnlockedModulesQuery()
  }

  const getCompletedLessons = () => {
    return fetchCompletedLessonsQuery()
  }

  const getSalesData = (offerId?: MaybeRefOrGetter<string>, source?: MaybeRefOrGetter<string>) => {
    return fetchSalesDataQuery(offerId, source)
  }

  const getNextIncompleteForAllCourses = () => {
    const { mutate, data, isPending, error } = createNextIncompleteLessonForAllCoursesMutation()

    return {
      mutate,
      data: computed(() => data.value || {}),
      isLoading: computed(() => isPending.value),
      error,
    }
  }

  return {
    getNextInCompleteLesson,
    getNextIncompleteForAllCourses,
    getUnlockedModules,
    getCompletedLessons,
    getSalesData,
  }
}
